import React from "react";
import { topicDictionary } from "../../Util/tileDictionary";
import * as analytics from "../../Util/Analytics/QuestionAnalytics";

const EXCLUDED_EXPORT_COLUMNS = new Set([
  "locationid",
  "locationorder",
  "parentlocationdesc",
  "parentlocationid",
  "iscensus",
  "locationtypeorder",
  "datavaluefootnote",
  "datavaluefootnotesymbol",
  "topicid",
  "pexcode",
  "locationalias",
]);

function normalizeKey(key) {
  return String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function shouldIncludeColumn(key) {
  return !EXCLUDED_EXPORT_COLUMNS.has(normalizeKey(key));
}

const ExportCsv = ({ subQuestionCSVData, graphCsvData, comparisonData, topicId, topicIdSet, year }) => {
  function handleExport() {
    analytics.selectExport({ exportType: "CSV" });
    
    let csvData = "";
    let fileName = "Profiles Explorer Survey Questions.csv";

    // If comparison data exists, export in the same format as standard single-year export
    // Each year's data becomes separate rows with the same columns
    if (comparisonData && comparisonData.Rows && comparisonData.Rows.length > 0) {
      const meta = comparisonData.Meta || {};
      const yearA = meta.Year || year;
      const yearB = meta.CompareYear;

      const rows = comparisonData.Rows;
      
      // Use the same column structure as the standard table data export
      const headers = [
        "Yr",
        "LocationTxt",
        "LocationAbbr",
        "LocationType",
        "NPercent",
        "LowCI",
        "HighCI",
        "ConfidenceInterval",
        "SampleSize",
        "QuestionId",
        "SubQuestion",
        "SubQuestionId"
      ].filter(shouldIncludeColumn);
      
      csvData = headers.join(",") + "\n";
      
      const isLocationCompare =
        (meta.LabelA || meta.LabelB) &&
        (!yearB || String(yearB) === String(yearA));

      const guessLocationType = (label) => {
        if (!label) return "";
        const lowered = String(label).toLowerCase();
        if (lowered.includes("nationwide") || lowered.includes("national")) return "National";
        if (/^[A-Z]{2}$/.test(String(label).trim())) return "State";
        return "";
      };

      const getLow = (data) => data?.LowCI ?? data?.CILow ?? data?.Lower ?? data?.LowerValue ?? "";
      const getHigh = (data) => data?.HighCI ?? data?.CIHigh ?? data?.Upper ?? data?.UpperValue ?? "";
      const buildCIText = (data, low, high) => {
        const direct = data?.CI ?? data?.ConfidenceInterval ?? "";
        if (direct !== null && direct !== undefined && String(direct).trim() !== "") return direct;
        if (low === null || low === undefined || low === "" || high === null || high === undefined || high === "") return "";
        return `${low} - ${high}`;
      };
      const getPercent = (data) => data?.Percent ?? data?.NPercent ?? "";
      const getN = (data) => data?.SampleSize ?? data?.N ?? "";

      if (isLocationCompare) {
        rows.forEach((row) => {
          const yearAData = row.YearA || {};
          const yearBData = row.YearB || {};
          const labelA = meta.LabelA || row.Location?.Desc || row.Location?.Abbr || "";
          const labelB = meta.LabelB || "";
          const abbrA = meta.AbbrA || row.Location?.Abbr || "";
          const abbrB = meta.AbbrB || "";
          const typeA = meta.TypeA || row.Location?.Type || guessLocationType(labelA);
          const typeB = meta.TypeB || guessLocationType(labelB);

          const lowA = getLow(yearAData);
          const highA = getHigh(yearAData);
          const lowB = getLow(yearBData);
          const highB = getHigh(yearBData);

          const rowValuesA = [
            yearA,
            JSON.stringify(labelA),
            JSON.stringify(abbrA),
            JSON.stringify(typeA),
            getPercent(yearAData),
            lowA,
            highA,
            buildCIText(yearAData, lowA, highA),
            getN(yearAData),
            JSON.stringify(row.QuestionId || meta.QuestionId || ""),
            JSON.stringify(row.SubQuestion || meta.SubQuestion || ""),
            JSON.stringify(row.SubQuestionId || meta.SubQuestionId || "")
          ];
          csvData += rowValuesA.join(",") + "\n";

          const rowValuesB = [
            yearA,
            JSON.stringify(labelB),
            JSON.stringify(abbrB),
            JSON.stringify(typeB),
            getPercent(yearBData),
            lowB,
            highB,
            buildCIText(yearBData, lowB, highB),
            getN(yearBData),
            JSON.stringify(row.QuestionId || meta.QuestionId || ""),
            JSON.stringify(row.SubQuestion || meta.SubQuestion || ""),
            JSON.stringify(row.SubQuestionId || meta.SubQuestionId || "")
          ];
          csvData += rowValuesB.join(",") + "\n";
        });

        fileName = `Profiles Explorer Comparison ${meta.LabelA || "Location A"} vs ${meta.LabelB || "Location B"}.csv`;
      } else {
        // Output each row twice - once for YearA, once for YearB
        rows.forEach((row) => {
          const location = row.Location?.Desc || row.Location?.Abbr || row.Label || "";
          const locationAbbr = row.Location?.Abbr || "";
          const locationType = row.Location?.Type || "";
          const yearAData = row.YearA || {};
          const yearBData = row.YearB || {};

          // Row for Year A
          const lowA = getLow(yearAData);
          const highA = getHigh(yearAData);
          const lowB = getLow(yearBData);
          const highB = getHigh(yearBData);

          const rowValuesA = [
            yearA,
            JSON.stringify(location),
            JSON.stringify(locationAbbr),
            JSON.stringify(locationType),
            getPercent(yearAData),
            lowA,
            highA,
            buildCIText(yearAData, lowA, highA),
            getN(yearAData),
            JSON.stringify(row.QuestionId || meta.QuestionId || ""),
            JSON.stringify(row.SubQuestion || meta.SubQuestion || ""),
            JSON.stringify(row.SubQuestionId || meta.SubQuestionId || "")
          ];
          csvData += rowValuesA.join(",") + "\n";

          // Row for Year B
          const rowValuesB = [
            yearB,
            JSON.stringify(location),
            JSON.stringify(locationAbbr),
            JSON.stringify(locationType),
            getPercent(yearBData),
            lowB,
            highB,
            buildCIText(yearBData, lowB, highB),
            getN(yearBData),
            JSON.stringify(row.QuestionId || meta.QuestionId || ""),
            JSON.stringify(row.SubQuestion || meta.SubQuestion || ""),
            JSON.stringify(row.SubQuestionId || meta.SubQuestionId || "")
          ];
          csvData += rowValuesB.join(",") + "\n";
        });

        fileName = `Profiles Explorer Comparison ${yearA} vs ${yearB}.csv`;
      }
    } else {
      // Standard export for single year data
      const csvSource = graphCsvData || subQuestionCSVData;
      const data = Object.keys(csvSource).includes("medianRangeData")
        ? csvSource.tableData
        : csvSource;

      const keys = Object.keys(data[0]).filter(shouldIncludeColumn);
      csvData =
        keys.join(",") +
        "\n" +
        data
          .map((obj, index) => {
            return keys
              .map((key) => {
                const value = obj[key];

                // Format the value if needed
                return value ? JSON.stringify(value) : "";
              })
              .join(",");
          })
          .join("\n");
    }

    // Create blob from csv
    const blob = new Blob([csvData], { type: "text/csv" });

    // Create a download link
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = fileName;

    // Trigger the download
    downloadLink.click();
  }
  const color = topicIdSet ? topicDictionary[topicId].color : "";
  return (
    <div>
      <button
        className="btn-info genBtn"
        onClick={handleExport}
        style={{
          color: "white",
          width: "200px",
          backgroundColor: color,
        }}
      >
        Export to CSV
      </button>
    </div>
  );
};

export default ExportCsv;
